import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Container,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useLoginMutation } from '../api/loginApi';
import { useTranslation } from 'react-i18next';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface LoginFormProps {
  isLoggedIn: boolean;
  setIsLoggedIn: (data: boolean) => void;
  onClose: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onClose, setIsLoggedIn }) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [login] = useLoginMutation();
  const { t } = useTranslation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await login({ email, password }).unwrap();
      const token = result.access_token;
      if (token) {
        localStorage.setItem('token', token);
        setIsLoggedIn(true);
        onClose();
      }
    } catch (err) {
      console.error('login failed:', err);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  return (
    <Container maxWidth="xs"> {/* XS pour garder petit comme ton Dialog */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#282c34',
          p: 2,
          borderRadius: 2,
          color: 'white',
        }}
      >
        <Box
          component="form"
          onSubmit={handleLogin}
          sx={{ mt: 1, width: '100%' }}
        >
          {/* Email Input */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('email')}
            name="email"
            autoFocus
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            InputProps={{
              style: {
                color: 'white',
                backgroundColor: '#282c34',
                borderRadius: 8,
                border: '1px solid grey',
              },
            }}
            InputLabelProps={{
              style: {
                color: 'lightgray',
              },
            }}
          />

          {/* Password Input */}
          <TextField
            margin="normal"
            required
            fullWidth
            size="small"
            name="password"
            label={t('password')}
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    sx={{ color: 'white' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
              style: {
                color: 'white',
                backgroundColor: '#282c34',
                borderRadius: 8,
                border: '1px solid grey',
              },
            }}
            InputLabelProps={{
              style: {
                color: 'lightgray',
              },
            }}
          />

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              type="submit"
              variant="outlined"
              sx={{
                borderRadius: 20,
                color: 'white',
                borderColor: 'cyan',
                '&:hover': {
                  borderColor: 'white',
                },
              }}
            >
              {t('login')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginForm;
